import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, TextField, Button, Snackbar,
  Alert, InputAdornment
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export default function PromocaoVisualizar() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [promo, setPromo] = useState({
    nome: '',
    descricao: '',
    dias_pagos: '',
    dias_ganhos: '',
    valor_diaria: '',
    ativa: true
  });

  const [datas, setDatas] = useState({
    retirada: '',
    devolucao: '',
    dias: 0
  });

  const [snackbar, setSnackbar] = useState({ open: false, mensagem: '', tipo: 'success' });

  useEffect(() => {
    const carregar = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/promocoes/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPromo({
          ...res.data,
          valor_diaria: res.data.valor_diaria?.toString().replace('.', ',')
        });
      } catch {
        setSnackbar({ open: true, mensagem: 'Erro ao carregar promoção.', tipo: 'error' });
      }
    };
    carregar();
  }, [id]);

  const calcularDias = (retiradaStr, devolucaoStr) => {
    const retirada = new Date(retiradaStr);
    const devolucao = new Date(devolucaoStr);
    if (!isNaN(retirada) && !isNaN(devolucao) && devolucao > retirada) {
      const msPorDia = 1000 * 60 * 60 * 24;
      let dias = Math.floor((devolucao - retirada) / msPorDia);
      const limite15h = new Date(devolucao);
      limite15h.setHours(15, 0, 0, 0);
      if (devolucao > limite15h) dias += 1;
      return dias;
    }
    return 0;
  };

  const handleDataChange = (e) => {
    const { name, value } = e.target;
    const novaData = { ...datas, [name]: value };
    novaData.dias = calcularDias(novaData.retirada, novaData.devolucao);
    setDatas(novaData);
  };

  const diaria = parseFloat(promo.valor_diaria?.replace(',', '.')) || 0;
  const dias = datas.dias;
  const ciclos = promo.dias_pagos && promo.dias_ganhos
    ? Math.floor(dias / (parseInt(promo.dias_pagos) + parseInt(promo.dias_ganhos)))
    : 0;
  const desconto = ciclos * (parseInt(promo.dias_ganhos) || 0) * diaria;
  const valorBruto = diaria * dias;
  const valorComDesconto = valorBruto - desconto;

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Container maxWidth="lg">
        <Box sx={{ backgroundColor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          {/* HEADER GRADIENT */}
          <Box sx={{
            background: 'linear-gradient(135deg, #1f1f1f 0%, #f78b00 100%)',
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: '#fff'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', flex: 1 }}>
              👁️ VISUALIZAR PROMOÇÃO
            </Typography>
          </Box>

          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* SEÇÃO 1: INFORMAÇÕES GERAIS */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1f1f1f', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                📋 INFORMAÇÕES GERAIS
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 3, 
                borderRadius: 1.5, 
                border: '1px solid #e0e0e0',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2
              }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>NOME</Typography>
                  <Typography variant="body1" sx={{ color: '#1f1f1f', fontWeight: 500 }}>{promo.nome}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>STATUS</Typography>
                  <Typography variant="body1" sx={{ color: promo.ativa ? '#4caf50' : '#999', fontWeight: 500 }}>
                    {promo.ativa ? '✓ Ativa' : '✗ Inativa'}
                  </Typography>
                </Box>
                <Box sx={{ gridColumn: { xs: '1fr', sm: '1 / -1' } }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>DESCRIÇÃO</Typography>
                  <Typography variant="body2" sx={{ color: '#1f1f1f' }}>{promo.descricao || '—'}</Typography>
                </Box>
              </Box>
            </Box>

            {/* SEÇÃO 2: CONFIGURAÇÃO DE PROMOÇÃO */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1f1f1f', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                🎁 CONFIGURAÇÃO DE PROMOÇÃO
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 3, 
                borderRadius: 1.5, 
                border: '1px solid #e0e0e0',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                gap: 2
              }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>DIAS PAGOS</Typography>
                  <Typography variant="body1" sx={{ color: '#1f1f1f', fontWeight: 500, fontSize: '1.3rem' }}>{promo.dias_pagos}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>DIAS GANHOS</Typography>
                  <Typography variant="body1" sx={{ color: '#f78b00', fontWeight: 500, fontSize: '1.3rem' }}>{promo.dias_ganhos}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>VALOR DA DIÁRIA</Typography>
                  <Typography variant="body1" sx={{ color: '#1f1f1f', fontWeight: 500, fontSize: '1.2rem' }}>R$ {promo.valor_diaria}</Typography>
                </Box>
              </Box>
            </Box>

            {/* SEÇÃO 3: SIMULAÇÃO DE DATAS */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1f1f1f', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                📅 SIMULAÇÃO DE DATAS
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 3, 
                borderRadius: 1.5, 
                border: '1px solid #e0e0e0',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2
              }}>
                <TextField
                  fullWidth
                  label="Data de Retirada"
                  name="retirada"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={datas.retirada}
                  onChange={handleDataChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      '&:hover fieldset': { borderColor: '#f78b00' },
                      '&.Mui-focused fieldset': { borderColor: '#f78b00', borderWidth: 2 }
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#f78b00' }
                  }}
                />
                <TextField
                  fullWidth
                  label="Data de Devolução"
                  name="devolucao"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={datas.devolucao}
                  onChange={handleDataChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      '&:hover fieldset': { borderColor: '#f78b00' },
                      '&.Mui-focused fieldset': { borderColor: '#f78b00', borderWidth: 2 }
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#f78b00' }
                  }}
                />
              </Box>
            </Box>

            {/* SEÇÃO 4: RESUMO DO DESCONTO (Condicional) */}
            {datas.retirada && datas.devolucao && datas.dias > 0 && promo.valor_diaria && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1f1f1f', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  💰 RESUMO DA PROMOÇÃO
                </Typography>
                <Box sx={{ 
                  backgroundColor: '#f8f9fa', 
                  p: 3, 
                  borderRadius: 1.5, 
                  border: '2px solid #f78b00',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2
                }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>PERÍODO</Typography>
                    <Typography variant="body2" sx={{ color: '#1f1f1f', fontWeight: 500 }}>
                      {new Date(datas.retirada).toLocaleDateString()} a {new Date(datas.devolucao).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>DIAS</Typography>
                    <Typography variant="body2" sx={{ color: '#1f1f1f', fontWeight: 500 }}>{datas.dias} dia(s)</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>VALOR BRUTO</Typography>
                    <Typography variant="body2" sx={{ color: '#1f1f1f', fontWeight: 500 }}>R$ {valorBruto.toFixed(2).replace('.', ',')}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>DESCONTO ESTIMADO</Typography>
                    <Typography variant="body2" sx={{ color: '#f78b00', fontWeight: 'bold' }}>R$ {desconto.toFixed(2).replace('.', ',')}</Typography>
                  </Box>
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>VALOR FINAL COM DESCONTO</Typography>
                    <Typography variant="h6" sx={{ color: '#f78b00', fontWeight: 'bold' }}>R$ {valorComDesconto.toFixed(2).replace('.', ',')}</Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* BOTÕES */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/cadastros/promocoes')}
                sx={{
                  borderColor: '#999',
                  color: '#999',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                VOLTAR
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate(`/promocoes/${id}/editar`)}
                sx={{
                  background: 'linear-gradient(135deg, #1f1f1f 0%, #f78b00 100%)',
                  color: '#fff',
                  fontWeight: 'bold',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0d0d0d 0%, #e67800 100%)'
                  }
                }}
              >
                EDITAR
              </Button>
            </Box>
          </Box>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbar.tipo} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} variant="filled">
            {snackbar.mensagem}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
